import { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { useIndustry, useIndustryLabels } from "../../context/IndustryContext";
import { getBranches, updateBranch } from "../../api/branches";
import {
  getShiftHoursConfig,
  updateShiftHoursConfig,
} from "../../api/attendance";
import { TENANT_INDUSTRY } from "../../utils/industryLabels";
import "bootstrap/dist/css/bootstrap.rtl.min.css";

const FACTORY_SHIFT_PRESETS = [
  {
    id: "factory-shift-a",
    nameAr: "وردية أ",
    default: { start: "06:00", end: "14:00" },
    thuFri: { start: "06:00", end: "14:00" },
  },
  {
    id: "factory-shift-b",
    nameAr: "وردية ب",
    default: { start: "14:00", end: "22:00" },
    thuFri: { start: "14:00", end: "22:00" },
  },
  {
    id: "factory-shift-c",
    nameAr: "وردية ج",
    default: { start: "22:00", end: "06:00" },
    thuFri: { start: "22:00", end: "06:00" },
  },
];

const SYSTEM_DEFAULTS = {
  MORNING: {
    default: { start: "11:00", end: "20:00" },
    thuFri: { start: "11:00", end: "21:00" },
  },
  EVENING: {
    default: { start: "15:00", end: "00:00" },
    thuFri: { start: "15:00", end: "01:00" },
  },
};

const OFFICE_DEFAULTS = { start: "10:00", end: "18:00" };
const WAREHOUSE_DEFAULTS = {
  start: "09:00",
  end: "19:00",
  workingHoursPerDay: 10,
};

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function cloneHours(source) {
  return {
    MORNING: {
      default: { ...source.MORNING.default },
      thuFri: { ...source.MORNING.thuFri },
    },
    EVENING: {
      default: { ...source.EVENING.default },
      thuFri: { ...source.EVENING.thuFri },
    },
  };
}

function resolveHours(raw) {
  if (
    raw?.MORNING?.default?.start &&
    raw?.MORNING?.default?.end &&
    raw?.MORNING?.thuFri?.start &&
    raw?.MORNING?.thuFri?.end &&
    raw?.EVENING?.default?.start &&
    raw?.EVENING?.default?.end &&
    raw?.EVENING?.thuFri?.start &&
    raw?.EVENING?.thuFri?.end
  ) {
    return cloneHours(raw);
  }
  return cloneHours(SYSTEM_DEFAULTS);
}

function formatRange(range) {
  if (!range?.start || !range?.end) return "—";
  return `${range.start} → ${range.end}`;
}

function emptyCustomShift() {
  return {
    id: "",
    nameAr: "",
    default: { start: "09:00", end: "17:00" },
    thuFri: { start: "09:00", end: "17:00" },
  };
}

const PERIOD_ROWS = [
  {
    shift: "MORNING",
    label: "صباحي",
    dayKey: "default",
    dayLabel: "سبت–أربعاء",
  },
  {
    shift: "MORNING",
    label: "صباحي",
    dayKey: "thuFri",
    dayLabel: "خميس/جمعة",
  },
  {
    shift: "EVENING",
    label: "مسائي",
    dayKey: "default",
    dayLabel: "سبت–أربعاء",
  },
  {
    shift: "EVENING",
    label: "مسائي",
    dayKey: "thuFri",
    dayLabel: "خميس/جمعة",
  },
];

function PeriodHoursEditor({ hours, onChange, disabled }) {
  return (
    <div className="row g-2">
      {PERIOD_ROWS.map((row) => (
        <div key={`${row.shift}-${row.dayKey}`} className="col-12 col-md-6">
          <label className="form-label small mb-1">
            {row.label} — {row.dayLabel}
          </label>
          <div className="d-flex gap-2">
            <input
              type="time"
              className="form-control form-control-sm"
              value={hours[row.shift][row.dayKey].start}
              onChange={(e) =>
                onChange(row.shift, row.dayKey, "start", e.target.value)
              }
              disabled={disabled}
            />
            <input
              type="time"
              className="form-control form-control-sm"
              value={hours[row.shift][row.dayKey].end}
              onChange={(e) =>
                onChange(row.shift, row.dayKey, "end", e.target.value)
              }
              disabled={disabled}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function PeriodHoursSummary({ hours }) {
  return (
    <div className="small">
      <div className="mb-2">
        <strong>صباحي:</strong> سبت–أربعاء {formatRange(hours.MORNING.default)}{" "}
        — خميس/جمعة {formatRange(hours.MORNING.thuFri)}
      </div>
      <div>
        <strong>مسائي:</strong> سبت–أربعاء {formatRange(hours.EVENING.default)}{" "}
        — خميس/جمعة {formatRange(hours.EVENING.thuFri)}
      </div>
    </div>
  );
}

export default function BranchShiftHours() {
  const { refreshData } = useData();
  const { industry } = useIndustry();
  const labels = useIndustryLabels();
  const isFactory = industry === TENANT_INDUSTRY.FACTORY;
  const [branches, setBranches] = useState([]);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [form, setForm] = useState(cloneHours(SYSTEM_DEFAULTS));

  const [editingOffice, setEditingOffice] = useState(false);
  const [officeForm, setOfficeForm] = useState(OFFICE_DEFAULTS);
  const [editingWarehouse, setEditingWarehouse] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState(WAREHOUSE_DEFAULTS);
  const [editingCashier, setEditingCashier] = useState(false);
  const [cashierForm, setCashierForm] = useState(cloneHours(SYSTEM_DEFAULTS));
  const [editingCustom, setEditingCustom] = useState(false);
  const [customForm, setCustomForm] = useState([]);
  const [savingGlobal, setSavingGlobal] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [branchData, config] = await Promise.all([
        getBranches(),
        getShiftHoursConfig(),
      ]);
      setBranches(Array.isArray(branchData) ? branchData : []);
      setGlobalConfig(config || null);
      if (config?.office) setOfficeForm({ ...config.office });
      if (config?.warehouse) {
        setWarehouseForm({
          start: config.warehouse.start,
          end: config.warehouse.end,
          workingHoursPerDay: Number(config.warehouse.workingHoursPerDay) || 10,
        });
      }
      if (config?.cashier) setCashierForm(resolveHours(config.cashier));
      setCustomForm(
        Array.isArray(config?.customShifts)
          ? config.customShifts.map((s) => ({
              id: s.id,
              nameAr: s.nameAr,
              default: { ...s.default },
              thuFri: { ...s.thuFri },
            }))
          : [],
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل مواعيد الورديات"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () =>
      branches.map((branch) => ({
        ...branch,
        effectiveHours: resolveHours(branch.timesheetShiftHours),
        hasCustom: Boolean(branch.timesheetShiftHours),
      })),
    [branches],
  );

  const startEdit = (branch) => {
    setError("");
    setSuccess("");
    setEditingId(branch.id);
    setForm(resolveHours(branch.timesheetShiftHours));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(cloneHours(SYSTEM_DEFAULTS));
  };

  const setRange = (shift, dayKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      [shift]: {
        ...prev[shift],
        [dayKey]: {
          ...prev[shift][dayKey],
          [field]: value,
        },
      },
    }));
  };

  const setCashierRange = (shift, dayKey, field, value) => {
    setCashierForm((prev) => ({
      ...prev,
      [shift]: {
        ...prev[shift],
        [dayKey]: {
          ...prev[shift][dayKey],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async (branchId) => {
    setError("");
    setSuccess("");
    setSavingId(branchId);
    try {
      await updateBranch(branchId, { timesheetShiftHours: form });
      setSuccess(`تم حفظ مواعيد الورديات ل${labels.site}`);
      setEditingId(null);
      await load();
      await refreshData?.();
    } catch (err) {
      setError(getApiErrorMessage(err, `تعذر حفظ مواعيد ${labels.site}`));
    } finally {
      setSavingId(null);
    }
  };

  const handleReset = async (branchId) => {
    const confirmed = window.confirm(
      `هل تريد إعادة مواعيد هذا ${labels.site.replace(/^ال/, "")} للمواعيد الافتراضية العامة؟\n\nتنبيه: الخطط الأسبوعية لأيام انتهت لن تتحول تلقائيًا. لو اتعمل تطبيق للمواعيد الجديدة على أيام فاتت، النظام ممكن يحسب تأخير أو انصراف مبكر وجزاءات على ناس التزمت بالمواعيد القديمة.`,
    );
    if (!confirmed) return;

    setError("");
    setSuccess("");
    setSavingId(branchId);
    try {
      await updateBranch(branchId, { timesheetShiftHours: null });
      setSuccess(`تمت إعادة المواعيد الافتراضية ل${labels.site}`);
      if (editingId === branchId) cancelEdit();
      await load();
      await refreshData?.();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إعادة المواعيد الافتراضية"));
    } finally {
      setSavingId(null);
    }
  };

  const saveGlobalSection = async (section, payload) => {
    setError("");
    setSuccess("");
    setSavingGlobal(section);
    try {
      const updated = await updateShiftHoursConfig(payload);
      setGlobalConfig(updated);
      if (updated?.office) setOfficeForm({ ...updated.office });
      if (updated?.warehouse) {
        setWarehouseForm({
          start: updated.warehouse.start,
          end: updated.warehouse.end,
          workingHoursPerDay:
            Number(updated.warehouse.workingHoursPerDay) || 10,
        });
      }
      if (updated?.cashier) setCashierForm(resolveHours(updated.cashier));
      setCustomForm(
        Array.isArray(updated?.customShifts)
          ? updated.customShifts.map((s) => ({
              id: s.id,
              nameAr: s.nameAr,
              default: { ...s.default },
              thuFri: { ...s.thuFri },
            }))
          : [],
      );
      setSuccess("تم حفظ المواعيد العامة");
      if (section === "office") setEditingOffice(false);
      if (section === "warehouse") setEditingWarehouse(false);
      if (section === "cashier") setEditingCashier(false);
      if (section === "custom") setEditingCustom(false);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ المواعيد العامة"));
    } finally {
      setSavingGlobal("");
    }
  };

  const cancelOffice = () => {
    setOfficeForm(
      globalConfig?.office
        ? { ...globalConfig.office }
        : { ...OFFICE_DEFAULTS },
    );
    setEditingOffice(false);
  };

  const cancelWarehouse = () => {
    setWarehouseForm(
      globalConfig?.warehouse
        ? {
            start: globalConfig.warehouse.start,
            end: globalConfig.warehouse.end,
            workingHoursPerDay:
              Number(globalConfig.warehouse.workingHoursPerDay) || 10,
          }
        : { ...WAREHOUSE_DEFAULTS },
    );
    setEditingWarehouse(false);
  };

  const cancelCashier = () => {
    setCashierForm(resolveHours(globalConfig?.cashier));
    setEditingCashier(false);
  };

  const cancelCustom = () => {
    setCustomForm(
      Array.isArray(globalConfig?.customShifts)
        ? globalConfig.customShifts.map((s) => ({
            id: s.id,
            nameAr: s.nameAr,
            default: { ...s.default },
            thuFri: { ...s.thuFri },
          }))
        : [],
    );
    setEditingCustom(false);
  };

  const updateCustomRow = (index, patch) => {
    setCustomForm((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const updateCustomRange = (index, dayKey, field, value) => {
    setCustomForm((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              [dayKey]: { ...row[dayKey], [field]: value },
            }
          : row,
      ),
    );
  };

  const office = globalConfig?.office || OFFICE_DEFAULTS;
  const warehouse = globalConfig?.warehouse || WAREHOUSE_DEFAULTS;
  const cashierHours = resolveHours(globalConfig?.cashier);
  const customShifts = Array.isArray(globalConfig?.customShifts)
    ? globalConfig.customShifts
    : [];

  return (
    <div className="container-fluid py-3" dir="rtl">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1">{labels.siteShiftHoursPage}</h4>
          <p className="text-muted mb-0 small">
            {isFactory
              ? `حدد مواعيد ${labels.sitePlural} (صباحي/مسائي أو ورديات أ/ب/ج) والمواعيد العامة للمكتب والمخزن، مع قوالب باسم حر للخطة الأسبوعية.`
              : `حدد مواعيد ${labels.sitePlural} (صباحي/مسائي) والمواعيد العامة للمكتب والمخزن والكاشيرية، مع ورديات بأسماء حرة تُستخدم كقوالب في الخطة الأسبوعية.`}{" "}
            التغيير يسري على الخطط الجديدة والأيام المتبقية — مش على أيام الشغل
            اللي خلصت، عشان متعملش جزاءات بأثر رجعي.
          </p>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      {loading ? (
        <div className="text-muted">جاري التحميل...</div>
      ) : (
        <>
          <h5 className="mb-2">المواعيد العامة</h5>
          <div className="row g-3 mb-4">
            <div className="col-12 col-xl-4">
              <div className="card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <strong>المكتب</strong>
                  <span className="badge bg-secondary">عام</span>
                </div>
                <div className="card-body">
                  {editingOffice ? (
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label small mb-1">من</label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={officeForm.start}
                          onChange={(e) =>
                            setOfficeForm((p) => ({
                              ...p,
                              start: e.target.value,
                            }))
                          }
                          disabled={Boolean(savingGlobal)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small mb-1">إلى</label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={officeForm.end}
                          onChange={(e) =>
                            setOfficeForm((p) => ({
                              ...p,
                              end: e.target.value,
                            }))
                          }
                          disabled={Boolean(savingGlobal)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="small">
                      <strong>الدوام:</strong> {formatRange(office)}
                    </div>
                  )}
                </div>
                <div className="card-footer d-flex flex-wrap gap-2">
                  {editingOffice ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={Boolean(savingGlobal)}
                        onClick={() =>
                          saveGlobalSection("office", { office: officeForm })
                        }
                      >
                        {savingGlobal === "office" ? "جاري الحفظ..." : "حفظ"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={Boolean(savingGlobal)}
                        onClick={cancelOffice}
                      >
                        إلغاء
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={Boolean(savingGlobal || savingId)}
                      onClick={() => {
                        setError("");
                        setSuccess("");
                        setOfficeForm({ ...office });
                        setEditingOffice(true);
                      }}
                    >
                      تعديل
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-4">
              <div className="card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <strong>المخزن</strong>
                  <span className="badge bg-secondary">عام</span>
                </div>
                <div className="card-body">
                  {editingWarehouse ? (
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label small mb-1">من</label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={warehouseForm.start}
                          onChange={(e) =>
                            setWarehouseForm((p) => ({
                              ...p,
                              start: e.target.value,
                            }))
                          }
                          disabled={Boolean(savingGlobal)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small mb-1">إلى</label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={warehouseForm.end}
                          onChange={(e) =>
                            setWarehouseForm((p) => ({
                              ...p,
                              end: e.target.value,
                            }))
                          }
                          disabled={Boolean(savingGlobal)}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label small mb-1">
                          ساعات العمل المطلوبة يومياً
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={24}
                          step={0.5}
                          className="form-control form-control-sm"
                          value={warehouseForm.workingHoursPerDay}
                          onChange={(e) =>
                            setWarehouseForm((p) => ({
                              ...p,
                              workingHoursPerDay: Number(e.target.value),
                            }))
                          }
                          disabled={Boolean(savingGlobal)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="small">
                      <div className="mb-1">
                        <strong>الافتراضي:</strong> {formatRange(warehouse)}
                      </div>
                      <div>
                        <strong>ساعات مطلوبة:</strong>{" "}
                        {warehouse.workingHoursPerDay}
                      </div>
                    </div>
                  )}
                </div>
                <div className="card-footer d-flex flex-wrap gap-2">
                  {editingWarehouse ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={Boolean(savingGlobal)}
                        onClick={() =>
                          saveGlobalSection("warehouse", {
                            warehouse: warehouseForm,
                          })
                        }
                      >
                        {savingGlobal === "warehouse"
                          ? "جاري الحفظ..."
                          : "حفظ"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={Boolean(savingGlobal)}
                        onClick={cancelWarehouse}
                      >
                        إلغاء
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={Boolean(savingGlobal || savingId)}
                      onClick={() => {
                        setError("");
                        setSuccess("");
                        setWarehouseForm({
                          start: warehouse.start,
                          end: warehouse.end,
                          workingHoursPerDay:
                            Number(warehouse.workingHoursPerDay) || 10,
                        });
                        setEditingWarehouse(true);
                      }}
                    >
                      تعديل
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-4">
              <div className="card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <strong>الكاشيرية</strong>
                  <span className="badge bg-secondary">عام</span>
                </div>
                <div className="card-body">
                  {editingCashier ? (
                    <PeriodHoursEditor
                      hours={cashierForm}
                      onChange={setCashierRange}
                      disabled={Boolean(savingGlobal)}
                    />
                  ) : (
                    <PeriodHoursSummary hours={cashierHours} />
                  )}
                </div>
                <div className="card-footer d-flex flex-wrap gap-2">
                  {editingCashier ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={Boolean(savingGlobal)}
                        onClick={() =>
                          saveGlobalSection("cashier", {
                            cashier: cashierForm,
                          })
                        }
                      >
                        {savingGlobal === "cashier" ? "جاري الحفظ..." : "حفظ"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={Boolean(savingGlobal)}
                        onClick={cancelCashier}
                      >
                        إلغاء
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={Boolean(savingGlobal || savingId)}
                      onClick={() => {
                        setError("");
                        setSuccess("");
                        setCashierForm(resolveHours(globalConfig?.cashier));
                        setEditingCashier(true);
                      }}
                    >
                      تعديل
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <h5 className="mb-2">ورديات مخصصة</h5>
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <div>
                <strong>قوالب باسم حر</strong>
                <div className="small text-muted">
                  تظهر كاختصارات في الخطة الأسبوعية لملء من–إلى
                  {isFactory ? " — للمصانع يُفضَّل ورديات أ / ب / ج" : ""}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {isFactory && !editingCustom && customShifts.length === 0 ? (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={Boolean(savingGlobal || savingId)}
                    onClick={() => {
                      setError("");
                      setSuccess("");
                      setCustomForm(
                        FACTORY_SHIFT_PRESETS.map((row) => ({
                          ...row,
                          default: { ...row.default },
                          thuFri: { ...row.thuFri },
                        })),
                      );
                      setEditingCustom(true);
                    }}
                  >
                    إضافة أ / ب / ج
                  </button>
                ) : null}
                <span className="badge bg-secondary">
                  {editingCustom ? customForm.length : customShifts.length}
                </span>
              </div>
            </div>
            <div className="card-body">
              {editingCustom ? (
                <div className="d-flex flex-column gap-3">
                  {customForm.length === 0 ? (
                    <div className="text-muted small">
                      لا توجد ورديات — أضف وردية جديدة
                    </div>
                  ) : null}
                  {customForm.map((row, index) => (
                    <div
                      key={row.id || `new-${index}`}
                      className="border rounded p-2"
                    >
                      <div className="row g-2 align-items-end">
                        <div className="col-12 col-md-4">
                          <label className="form-label small mb-1">
                            اسم الوردية
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.nameAr}
                            maxLength={80}
                            onChange={(e) =>
                              updateCustomRow(index, {
                                nameAr: e.target.value,
                              })
                            }
                            disabled={Boolean(savingGlobal)}
                            placeholder="مثال: وردية ظهر"
                          />
                        </div>
                        <div className="col-12 col-md-3">
                          <label className="form-label small mb-1">
                            سبت–أربعاء
                          </label>
                          <div className="d-flex gap-2">
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              value={row.default.start}
                              onChange={(e) =>
                                updateCustomRange(
                                  index,
                                  "default",
                                  "start",
                                  e.target.value,
                                )
                              }
                              disabled={Boolean(savingGlobal)}
                            />
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              value={row.default.end}
                              onChange={(e) =>
                                updateCustomRange(
                                  index,
                                  "default",
                                  "end",
                                  e.target.value,
                                )
                              }
                              disabled={Boolean(savingGlobal)}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-3">
                          <label className="form-label small mb-1">
                            خميس/جمعة
                          </label>
                          <div className="d-flex gap-2">
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              value={row.thuFri.start}
                              onChange={(e) =>
                                updateCustomRange(
                                  index,
                                  "thuFri",
                                  "start",
                                  e.target.value,
                                )
                              }
                              disabled={Boolean(savingGlobal)}
                            />
                            <input
                              type="time"
                              className="form-control form-control-sm"
                              value={row.thuFri.end}
                              onChange={(e) =>
                                updateCustomRange(
                                  index,
                                  "thuFri",
                                  "end",
                                  e.target.value,
                                )
                              }
                              disabled={Boolean(savingGlobal)}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-2">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm w-100"
                            disabled={Boolean(savingGlobal)}
                            onClick={() =>
                              setCustomForm((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }
                          >
                            حذف
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm align-self-start"
                    disabled={Boolean(savingGlobal)}
                    onClick={() =>
                      setCustomForm((prev) => [...prev, emptyCustomShift()])
                    }
                  >
                    + إضافة وردية
                  </button>
                </div>
              ) : customShifts.length === 0 ? (
                <div className="text-muted small">لا توجد ورديات مخصصة بعد</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>الاسم</th>
                        <th>سبت–أربعاء</th>
                        <th>خميس/جمعة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customShifts.map((row) => (
                        <tr key={row.id}>
                          <td>{row.nameAr}</td>
                          <td>{formatRange(row.default)}</td>
                          <td>{formatRange(row.thuFri)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card-footer d-flex flex-wrap gap-2">
              {editingCustom ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={Boolean(savingGlobal)}
                    onClick={() =>
                      saveGlobalSection("custom", {
                        customShifts: customForm.map((row) => ({
                          ...(row.id ? { id: row.id } : {}),
                          nameAr: row.nameAr,
                          default: row.default,
                          thuFri: row.thuFri,
                        })),
                      })
                    }
                  >
                    {savingGlobal === "custom" ? "جاري الحفظ..." : "حفظ"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={Boolean(savingGlobal)}
                    onClick={cancelCustom}
                  >
                    إلغاء
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={Boolean(savingGlobal || savingId)}
                  onClick={() => {
                    setError("");
                    setSuccess("");
                    setCustomForm(
                      customShifts.map((s) => ({
                        id: s.id,
                        nameAr: s.nameAr,
                        default: { ...s.default },
                        thuFri: { ...s.thuFri },
                      })),
                    );
                    setEditingCustom(true);
                  }}
                >
                  تعديل الورديات المخصصة
                </button>
              )}
            </div>
          </div>

          <h5 className="mb-2">الفروع</h5>
          <div className="row g-3">
            {rows.map((branch) => {
              const isEditing = editingId === branch.id;
              const busy = savingId === branch.id;
              const hours = isEditing ? form : branch.effectiveHours;

              return (
                <div key={branch.id} className="col-12 col-xl-6">
                  <div className="card h-100">
                    <div className="card-header d-flex justify-content-between align-items-center gap-2">
                      <div>
                        <strong>{branch.name}</strong>
                        <div className="small text-muted">{branch.code}</div>
                      </div>
                      <span
                        className={`badge ${
                          branch.hasCustom ? "bg-primary" : "bg-secondary"
                        }`}
                      >
                        {branch.hasCustom ? "مخصص" : "افتراضي"}
                      </span>
                    </div>
                    <div className="card-body">
                      {isEditing ? (
                        <PeriodHoursEditor
                          hours={hours}
                          onChange={setRange}
                          disabled={busy}
                        />
                      ) : (
                        <PeriodHoursSummary hours={hours} />
                      )}
                    </div>
                    <div className="card-footer d-flex flex-wrap gap-2">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => handleSave(branch.id)}
                            disabled={busy}
                          >
                            {busy ? "جاري الحفظ..." : "حفظ"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={cancelEdit}
                            disabled={busy}
                          >
                            إلغاء
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => startEdit(branch)}
                            disabled={Boolean(savingId || savingGlobal)}
                          >
                            تعديل المواعيد
                          </button>
                          {branch.hasCustom ? (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => handleReset(branch.id)}
                              disabled={Boolean(savingId || savingGlobal)}
                            >
                              إعادة للافتراضي
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 ? (
              <div className="col-12 text-muted">لا توجد فروع مسجلة</div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
